import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Check, X, Users, Server as ServerIcon, ThumbsUp, UserPlus, Star, Trash2, Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../utils/api';
import { getAuth } from '../utils/auth';

export default function Admin() {
  const navigate = useNavigate();
  const { user } = getAuth();
  const [pendingServers, setPendingServers] = useState([]);
  const [allServers, setAllServers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('approvals');
  const [serverSearch, setServerSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.email !== 'owner@mallurealm.com')) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchPendingServers();
    fetchStats();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.categories);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    try {
      const response = await api.post('/admin/categories', { name: newCategoryName });
      setCategories([...categories, response.data]);
      setNewCategoryName('');
      toast.success('Category added successfully');
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      setCategories(categories.filter(c => c.id !== id));
      toast.success('Category deleted');
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const fetchPendingServers = async () => {
    try {
      const response = await api.get('/admin/servers/pending');
      setPendingServers(response.data.servers);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch pending servers');
      setLoading(false);
    }
  };

  const fetchAllServers = async () => {
    try {
      const response = await api.get('/admin/servers/all');
      setAllServers(response.data.servers);
    } catch (error) {
      toast.error('Failed to fetch servers');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setAllUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleApprove = async (serverId) => {
    try {
      await api.post(`/admin/servers/${serverId}/approve`);
      toast.success('Server approved successfully');
      fetchPendingServers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to approve server');
    }
  };

  const handleReject = async (serverId) => {
    try {
      await api.post(`/admin/servers/${serverId}/reject`);
      toast.success('Server rejected');
      fetchPendingServers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to reject server');
    }
  };

  const handleFeature = async (serverId, featured) => {
    try {
      if (featured) {
        await api.post(`/admin/servers/${serverId}/unfeature`);
        toast.success('Server unfeatured');
      } else {
        await api.post(`/admin/servers/${serverId}/feature`);
        toast.success('Server featured successfully');
      }
      fetchAllServers();
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const handleDeleteServer = async (serverId) => {
    if (!window.confirm('Are you sure you want to delete this server?')) return;
    
    try {
      await api.delete(`/servers/${serverId}`);
      toast.success('Server deleted');
      fetchAllServers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete server');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchAllUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  useEffect(() => {
    if (activeTab === 'servers') {
      fetchAllServers();
    } else if (activeTab === 'users') {
      fetchAllUsers();
    } else if (activeTab === 'categories') {
      fetchCategories();
    }
  }, [activeTab]);

  const filteredServers = allServers.filter(server => 
    server.name.toLowerCase().includes(serverSearch.toLowerCase()) ||
    server.ip_address.toLowerCase().includes(serverSearch.toLowerCase()) ||
    server.category.toLowerCase().includes(serverSearch.toLowerCase())
  );

  const filteredUsers = allUsers.filter(u => 
    u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.role.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen noise-bg">
      <Navbar />
      
      <div className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-10 h-10 text-[#00E055]" />
                  <h1 className="text-4xl md:text-5xl font-black tracking-tighter uppercase" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid="admin-title">
                    ADMIN <span className="text-[#00E055]">PANEL</span>
                  </h1>
                </div>
                <p className="text-muted-foreground">Manage server approvals and platform statistics</p>
              </div>
              
              {user && user.email === 'owner@mallurealm.com' && (
                <Link to="/admin/register">
                  <Button className="bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold gap-2" data-testid="register-admin-button">
                    <UserPlus className="w-5 h-5" />
                    CREATE ADMIN
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
              <div className="bg-[#0A0A0A] border border-border rounded-lg p-6" data-testid="stat-users">
                <div className="flex items-center gap-2 text-[#00E055] mb-2">
                  <Users className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase">USERS</span>
                </div>
                <p className="text-3xl font-black">{stats.total_users}</p>
              </div>

              <div className="bg-[#0A0A0A] border border-border rounded-lg p-6" data-testid="stat-servers">
                <div className="flex items-center gap-2 text-[#0CB7E1] mb-2">
                  <ServerIcon className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase">SERVERS</span>
                </div>
                <p className="text-3xl font-black">{stats.total_servers}</p>
              </div>

              <div className="bg-[#0A0A0A] border border-border rounded-lg p-6" data-testid="stat-votes">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <ThumbsUp className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase">TOTAL VOTES</span>
                </div>
                <p className="text-3xl font-black">{stats.total_votes}</p>
              </div>

              <div className="bg-[#0A0A0A] border border-border rounded-lg p-6" data-testid="stat-pending">
                <div className="flex items-center gap-2 text-yellow-400 mb-2">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase">PENDING</span>
                </div>
                <p className="text-3xl font-black">{stats.pending_servers}</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-[#0A0A0A] border border-border">
              <TabsTrigger value="approvals" className="data-[state=active]:bg-[#00E055] data-[state=active]:text-black">
                Pending Approvals
              </TabsTrigger>
              <TabsTrigger value="servers" className="data-[state=active]:bg-[#00E055] data-[state=active]:text-black">
                All Servers
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-[#00E055] data-[state=active]:text-black">
                User Management
              </TabsTrigger>
              <TabsTrigger value="categories" className="data-[state=active]:bg-[#00E055] data-[state=active]:text-black">
                Categories
              </TabsTrigger>
            </TabsList>

            {/* Pending Approvals Tab */}
            <TabsContent value="approvals">
              <h2 className="text-2xl font-bold tracking-tight mb-6" style={{fontFamily: 'Unbounded, sans-serif'}}>
                PENDING APPROVALS
              </h2>
              
              {loading ? (
                <div className="text-center py-20">
                  <div className="inline-block w-12 h-12 border-4 border-[#00E055] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : pendingServers.length > 0 ? (
                <div className="space-y-4" data-testid="pending-servers-list">
                  {pendingServers.map((server) => (
                    <div key={server.id} className="bg-[#0A0A0A] border border-yellow-500/20 rounded-lg p-6" data-testid={`pending-server-${server.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-2" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid={`server-name-${server.id}`}>
                            {server.name}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">{server.description}</p>
                          <div className="flex flex-wrap gap-2">
                            <span 
                              className="px-3 py-1 text-[10px] font-black tracking-widest uppercase bg-[#00E055]/10 text-[#00E055] border border-[#00E055]/20 rounded-sm flex items-center gap-1.5 shadow-[0_0_15px_rgba(0,224,85,0.05)]"
                              style={{ fontFamily: 'Unbounded, sans-serif' }}
                            >
                              <span className="w-1 h-1 rounded-full bg-[#00E055] shadow-[0_0_5px_#00E055]" />
                              {server.category}
                            </span>
                            <span className="px-3 py-1 text-[10px] font-bold tracking-widest uppercase bg-white/10 text-white/60 border border-white/20 rounded-sm" data-testid={`server-ip-${server.id}`}>
                              {server.ip_address}:{server.port}
                            </span>
                            <span 
                              className="px-3 py-1 text-[10px] font-bold tracking-widest uppercase bg-white/5 text-white/40 border border-white/10 rounded-sm"
                              style={{ fontFamily: 'Unbounded, sans-serif' }}
                            >
                              {server.version}
                            </span>
                          </div>
                          {server.website && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Website: <a href={server.website} target="_blank" rel="noopener noreferrer" className="text-[#00E055] hover:underline">{server.website}</a>
                            </p>
                          )}
                          {server.discord && (
                            <p className="text-xs text-muted-foreground">
                              Discord: <a href={server.discord} target="_blank" rel="noopener noreferrer" className="text-[#00E055] hover:underline">{server.discord}</a>
                            </p>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApprove(server.id)}
                            className="bg-green-500 hover:bg-green-600 text-white gap-2"
                            data-testid={`approve-button-${server.id}`}
                          >
                            <Check className="w-4 h-4" />
                            APPROVE
                          </Button>
                          <Button
                            onClick={() => handleReject(server.id)}
                            variant="destructive"
                            className="gap-2"
                            data-testid={`reject-button-${server.id}`}
                          >
                            <X className="w-4 h-4" />
                            REJECT
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-[#0A0A0A] border border-border rounded-lg">
                  <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground text-lg">No pending servers to review</p>
                </div>
              )}
            </TabsContent>

            {/* All Servers Tab */}
            <TabsContent value="servers">
              <h2 className="text-2xl font-bold tracking-tight mb-6" style={{fontFamily: 'Unbounded, sans-serif'}}>
                ALL SERVERS
              </h2>

              <div className="mb-6 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search servers..."
                  className="w-full bg-[#0A0A0A] border border-border rounded-lg py-2 pl-10 pr-4 focus:border-[#00E055] outline-none transition-colors"
                  value={serverSearch}
                  onChange={(e) => setServerSearch(e.target.value)}
                />
              </div>
              
              <div className="space-y-3">
                {filteredServers.map((server) => (
                  <div key={server.id} className="bg-[#0A0A0A] border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold">{server.name}</h3>
                          {server.is_featured && (
                            <span className="px-2 py-1 text-xs font-bold bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-sm flex items-center gap-1">
                              <Star className="w-3 h-3 fill-yellow-400" />
                              FEATURED
                            </span>
                          )}
                          <span className={`px-2 py-1 text-xs font-bold uppercase rounded-sm ${
                            server.status === 'approved' ? 'bg-green-500/10 text-green-400' :
                            server.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                            'bg-red-500/10 text-red-400'
                          }`}>
                            {server.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{server.ip_address}:{server.port} • {server.category} • {server.vote_count} votes</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleFeature(server.id, server.is_featured)}
                          variant="outline"
                          size="sm"
                          className={server.is_featured ? 'text-yellow-400 border-yellow-500/20' : ''}
                        >
                          <Star className={`w-4 h-4 ${server.is_featured ? 'fill-yellow-400' : ''}`} />
                        </Button>
                        <Button
                          onClick={() => handleDeleteServer(server.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* User Management Tab */}
            <TabsContent value="users">
              <h2 className="text-2xl font-bold tracking-tight mb-6" style={{fontFamily: 'Unbounded, sans-serif'}}>
                USER MANAGEMENT
              </h2>

              <div className="mb-6 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search users..."
                  className="w-full bg-[#0A0A0A] border border-border rounded-lg py-2 pl-10 pr-4 focus:border-[#00E055] outline-none transition-colors"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
              </div>
              
              <div className="space-y-3">
                {filteredUsers.map((u) => (
                  <div key={u.id} className="bg-[#0A0A0A] border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-bold">{u.email}</p>
                          <span className={`px-2 py-1 text-xs font-bold uppercase rounded-sm ${
                            u.role === 'admin' ? 'bg-red-500/10 text-red-400' :
                            u.role === 'server_owner' ? 'bg-blue-500/10 text-blue-400' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {u.role}
                          </span>
                          {u.email === 'owner@mallurealm.com' && (
                            <span className="px-2 py-1 text-xs font-bold bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-sm">
                              OWNER
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Joined: {new Date(u.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      {u.email !== 'owner@mallurealm.com' && (
                        <Button
                          onClick={() => handleDeleteUser(u.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Category Management Tab */}
            <TabsContent value="categories">
              <h2 className="text-2xl font-bold tracking-tight mb-6" style={{fontFamily: 'Unbounded, sans-serif'}}>
                CATEGORY MANAGEMENT
              </h2>

              <form onSubmit={handleAddCategory} className="mb-8 flex gap-2">
                <input
                  type="text"
                  placeholder="New category name..."
                  className="flex-1 bg-[#0A0A0A] border border-border rounded-lg py-2 px-4 focus:border-[#00E055] outline-none transition-colors"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
                <Button type="submit" className="bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold">
                  ADD CATEGORY
                </Button>
              </form>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((cat) => (
                  <div key={cat.id} className="bg-[#0A0A0A] border border-border rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <p className="font-bold uppercase tracking-wider">{cat.name}</p>
                      <p className="text-xs text-muted-foreground">{cat.slug}</p>
                    </div>
                    <Button
                      onClick={() => handleDeleteCategory(cat.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
